import { b2ContactID, b2ContactPoint, b2Manifold } from "./collision.js";
import { b2TimeOfImpact } from "./collision_algorithms.js";
import { b2Math, b2Vec2 } from "./common/math.js";
import { b2CircleShape, b2PolygonShape, b2Shape } from "./shapes.js";
import { b2Collision } from "./collision_algorithms.js";

export class b2ContactEdge {
  constructor() {
    this.other = null;
    this.prev = null;
    this.contact = null;
    this.next = null;
  }
}

export class b2ContactRegister {
  constructor() {
    this.primary = false;
    this.createFcn = null;
    this.destroyFcn = null;
  }
}

export class b2ContactResult {
  constructor() {
    this.position = new b2Vec2();
    this.shape1 = null;
    this.shape2 = null;
    this.normalImpulse = 0;
    this.normal = new b2Vec2();
    this.tangentImpulse = 0;
    this.id = new b2ContactID();
  }
}

export class b2ContactListener {
  Add() {}

  Remove() {}

  Persist() {}

  Result() {}
}

export class b2Contact {
  static s_registers = null;
  static e_nonSolidFlag = 1;
  static e_slowFlag = 2;
  static e_islandFlag = 4;
  static e_toiFlag = 8;
  static e_touchingFlag = 16;
  static e_enabledFlag = 32;
  static e_filterFlag = 64;
  static s_initialized = false;

  constructor(shape1 = null, shape2 = null) {
    this.m_shape1 = null;
    this.m_shape2 = null;
    this.m_fixtureA = null;
    this.m_fixtureB = null;
    this.m_prev = null;
    this.m_toi = 0;
    this.m_next = null;
    this.m_friction = 0;
    this.m_manifoldCount = 0;
    this.m_node1 = new b2ContactEdge();
    this.m_node2 = new b2ContactEdge();
    this.m_nodeA = this.m_node1;
    this.m_nodeB = this.m_node2;
    this.m_restitution = 0;
    this.m_flags = 0;
    this.m_manifold = new b2Manifold();
    this.m_oldManifold = new b2Manifold();
    if (!shape1 || !shape2) {
      return;
    }
    if (shape1.IsSensor() || shape2.IsSensor()) {
      this.m_flags |= b2Contact.e_nonSolidFlag;
    }
    this.m_shape1 = shape1;
    this.m_shape2 = shape2;
    this.m_manifoldCount = 0;
    this.m_friction = Math.sqrt(this.m_shape1.m_friction * this.m_shape2.m_friction);
    this.m_restitution = b2Math.b2Max(this.m_shape1.m_restitution, this.m_shape2.m_restitution);
  }

  static InitializeRegisters() {
    b2Contact.s_registers = new Array(b2Shape.e_shapeTypeCount);
    for (let i = 0; i < b2Shape.e_shapeTypeCount; i += 1) {
      b2Contact.s_registers[i] = new Array(b2Shape.e_shapeTypeCount);
      for (let j = 0; j < b2Shape.e_shapeTypeCount; j += 1) {
        b2Contact.s_registers[i][j] = new b2ContactRegister();
      }
    }
    b2Contact.AddType(b2CircleContact.Create, b2CircleContact.Destroy, b2Shape.e_circleShape, b2Shape.e_circleShape);
    b2Contact.AddType(b2PolyAndCircleContact.Create, b2PolyAndCircleContact.Destroy, b2Shape.e_polygonShape, b2Shape.e_circleShape);
    b2Contact.AddType(b2PolygonContact.Create, b2PolygonContact.Destroy, b2Shape.e_polygonShape, b2Shape.e_polygonShape);
  }

  static Destroy(contact, allocator) {
    void allocator;
    if (contact.m_manifoldCount > 0) {
      contact.m_shape1.m_body?.WakeUp?.();
      contact.m_shape2.m_body?.WakeUp?.();
    }
    const type1 = contact.m_shape1.m_type;
    const type2 = contact.m_shape2.m_type;
    const register = b2Contact.s_registers[type1][type2];
    register.destroyFcn(contact, allocator);
  }

  static AddType(createFcn, destroyFcn, type1, type2) {
    b2Contact.s_registers[type1][type2].createFcn = createFcn;
    b2Contact.s_registers[type1][type2].destroyFcn = destroyFcn;
    b2Contact.s_registers[type1][type2].primary = true;
    if (type1 !== type2) {
      b2Contact.s_registers[type2][type1].createFcn = createFcn;
      b2Contact.s_registers[type2][type1].destroyFcn = destroyFcn;
      b2Contact.s_registers[type2][type1].primary = false;
    }
  }

  static Create(shape1, shape2, allocator) {
    void allocator;
    if (b2Contact.s_initialized === false) {
      b2Contact.InitializeRegisters();
      b2Contact.s_initialized = true;
    }
    const type1 = shape1.m_type;
    const type2 = shape2.m_type;
    const register = b2Contact.s_registers[type1][type2];
    const createFcn = register.createFcn;
    if (createFcn != null) {
      if (register.primary) {
        return createFcn(shape1, shape2, allocator);
      }
      const contact = createFcn(shape2, shape1, allocator);
      for (let i = 0; i < contact.m_manifoldCount; i += 1) {
        const manifold = contact.GetManifolds()[i];
        manifold.normal = manifold.normal.Negative();
      }
      return contact;
    }
    return null;
  }

  IsSolid() {
    return (this.m_flags & b2Contact.e_nonSolidFlag) === 0;
  }

  GetManifold() {
    return this.m_manifold;
  }

  IsTouching() {
    return (this.m_flags & b2Contact.e_touchingFlag) === b2Contact.e_touchingFlag;
  }

  SetSensor(sensor) {
    if (sensor) {
      this.m_flags |= b2Contact.e_nonSolidFlag;
    } else {
      this.m_flags &= ~b2Contact.e_nonSolidFlag;
    }
  }

  IsSensor() {
    return (this.m_flags & b2Contact.e_nonSolidFlag) === b2Contact.e_nonSolidFlag;
  }

  SetEnabled(flag) {
    if (flag) {
      this.m_flags |= b2Contact.e_enabledFlag;
    } else {
      this.m_flags &= ~b2Contact.e_enabledFlag;
    }
  }

  IsEnabled() {
    return (this.m_flags & b2Contact.e_enabledFlag) === b2Contact.e_enabledFlag;
  }

  GetFixtureA() {
    return this.m_fixtureA;
  }

  GetFixtureB() {
    return this.m_fixtureB;
  }

  FlagForFiltering() {
    this.m_flags |= b2Contact.e_filterFlag;
  }

  GetShape1() {
    return this.m_shape1;
  }

  GetShape2() {
    return this.m_shape2;
  }

  GetNext() {
    return this.m_next;
  }

  GetManifoldCount() {
    return this.m_manifoldCount;
  }

  GetManifolds() {
    return null;
  }

  ComputeTOI(sweepA, sweepB) {
    return b2TimeOfImpact.TimeOfImpact(this.m_shape1, sweepA, this.m_shape2, sweepB);
  }

  Update(listener) {
    const oldCount = this.m_manifoldCount;
    this.Evaluate(listener);
    const newCount = this.m_manifoldCount;
    const body1 = this.m_shape1.m_body;
    const body2 = this.m_shape2.m_body;
    if (newCount === 0 && oldCount > 0) {
      body1?.WakeUp?.();
      body2?.WakeUp?.();
    }
    if (body1?.IsStatic?.() || body1?.IsBullet?.() || body2?.IsStatic?.() || body2?.IsBullet?.()) {
      this.m_flags &= ~b2Contact.e_slowFlag;
    } else {
      this.m_flags |= b2Contact.e_slowFlag;
    }
  }

  Reset(fixtureA, fixtureB) {
    this.m_flags = b2Contact.e_enabledFlag;
    if (!fixtureA || !fixtureB) {
      this.m_fixtureA = null;
      this.m_fixtureB = null;
      this.m_shape1 = null;
      this.m_shape2 = null;
      this.m_manifoldCount = 0;
      return;
    }
    if (fixtureA.IsSensor() || fixtureB.IsSensor()) {
      this.m_flags |= b2Contact.e_nonSolidFlag;
    }
    const bodyA = fixtureA.GetBody();
    const bodyB = fixtureB.GetBody();
    if (bodyA.GetType() !== 2 || bodyA.IsBullet() || bodyB.GetType() !== 2 || bodyB.IsBullet()) {
      this.m_flags &= ~b2Contact.e_slowFlag;
    } else {
      this.m_flags |= b2Contact.e_slowFlag;
    }
    this.m_fixtureA = fixtureA;
    this.m_fixtureB = fixtureB;
    this.m_shape1 = fixtureA.GetShape();
    this.m_shape2 = fixtureB.GetShape();
    this.m_shape1.m_body = bodyA;
    this.m_shape2.m_body = bodyB;
    this.m_friction = Math.sqrt(this.m_shape1.m_friction * this.m_shape2.m_friction);
    this.m_restitution = b2Math.b2Max(this.m_shape1.m_restitution, this.m_shape2.m_restitution);
    if (this.m_shape1.m_p && !this.m_shape1.m_localPosition) {
      this.m_shape1.m_localPosition = this.m_shape1.m_p;
    }
    if (this.m_shape2.m_p && !this.m_shape2.m_localPosition) {
      this.m_shape2.m_localPosition = this.m_shape2.m_p;
    }
    this.m_manifold.pointCount = 0;
    this.m_manifold.m_pointCount = 0;
    this.m_oldManifold?.Reset?.();
    this.m_manifoldCount = 0;
    this.m_prev = null;
    this.m_next = null;
    this.m_nodeA.contact = null;
    this.m_nodeA.prev = null;
    this.m_nodeA.next = null;
    this.m_nodeA.other = null;
    this.m_nodeB.contact = null;
    this.m_nodeB.prev = null;
    this.m_nodeB.next = null;
    this.m_nodeB.other = null;
  }

  Evaluate() {}
}

export class b2NullContact extends b2Contact {
  Evaluate() {}

  GetManifolds() {
    return null;
  }
}

function evaluateContactListener(contact, oldManifold, manifold, body1, body2, shape1, shape2, friction, restitution, listener) {
  const persisted = [false, false];
  const contactPoint = new b2ContactPoint();
  contactPoint.shape1 = shape1;
  contactPoint.shape2 = shape2;
  contactPoint.friction = friction;
  contactPoint.restitution = restitution;
  if (manifold.pointCount > 0) {
    for (let i = 0; i < manifold.pointCount; i += 1) {
      const point = manifold.points[i];
      point.normalImpulse = 0;
      point.tangentImpulse = 0;
      let found = false;
      const idKey = point.id._key;
      for (let j = 0; j < oldManifold.pointCount; j += 1) {
        if (persisted[j]) {
          continue;
        }
        const oldPoint = oldManifold.points[j];
        if (oldPoint.id._key === idKey) {
          persisted[j] = true;
          point.normalImpulse = oldPoint.normalImpulse;
          point.tangentImpulse = oldPoint.tangentImpulse;
          found = true;
          if (listener != null) {
            contactPoint.position = body1.GetWorldPoint(point.localPoint1);
            const velocity1 = body1.GetLinearVelocityFromLocalPoint(point.localPoint1);
            const velocity2 = body2.GetLinearVelocityFromLocalPoint(point.localPoint2);
            contactPoint.velocity.Set(velocity2.x - velocity1.x, velocity2.y - velocity1.y);
            contactPoint.normal.SetV(manifold.normal);
            contactPoint.separation = point.separation;
            contactPoint.id.key = idKey;
            listener.Persist(contactPoint);
          }
          break;
        }
      }
      if (!found && listener != null) {
        contactPoint.position = body1.GetWorldPoint(point.localPoint1);
        const velocity1 = body1.GetLinearVelocityFromLocalPoint(point.localPoint1);
        const velocity2 = body2.GetLinearVelocityFromLocalPoint(point.localPoint2);
        contactPoint.velocity.Set(velocity2.x - velocity1.x, velocity2.y - velocity1.y);
        contactPoint.normal.SetV(manifold.normal);
        contactPoint.separation = point.separation;
        contactPoint.id.key = idKey;
        listener.Add(contactPoint);
      }
    }
    contact.m_manifoldCount = 1;
  } else {
    contact.m_manifoldCount = 0;
  }
  if (listener == null) {
    return;
  }
  for (let i = 0; i < oldManifold.pointCount; i += 1) {
    if (persisted[i]) {
      continue;
    }
    const oldPoint = oldManifold.points[i];
    contactPoint.position = body1.GetWorldPoint(oldPoint.localPoint1);
    const velocity1 = body1.GetLinearVelocityFromLocalPoint(oldPoint.localPoint1);
    const velocity2 = body2.GetLinearVelocityFromLocalPoint(oldPoint.localPoint2);
    contactPoint.velocity.Set(velocity2.x - velocity1.x, velocity2.y - velocity1.y);
    contactPoint.normal.SetV(oldManifold.normal);
    contactPoint.separation = oldPoint.separation;
    contactPoint.id.key = oldPoint.id._key;
    listener.Remove(contactPoint);
  }
}

function fixtureType(fixtureOrShape) {
  if (typeof fixtureOrShape?.GetType === "function") {
    return fixtureOrShape.GetType();
  }
  return fixtureOrShape?.m_type ?? -1;
}

export class b2ContactFactory {
  constructor(allocator = null) {
    this.m_allocator = allocator;
    this.InitializeRegisters();
  }

  AddType(createFcn, destroyFcn, type1, type2) {
    this.m_registers[type1][type2].createFcn = createFcn;
    this.m_registers[type1][type2].destroyFcn = destroyFcn;
    this.m_registers[type1][type2].primary = true;
    if (type1 !== type2) {
      this.m_registers[type2][type1].createFcn = createFcn;
      this.m_registers[type2][type1].destroyFcn = destroyFcn;
      this.m_registers[type2][type1].primary = false;
    }
  }

  InitializeRegisters() {
    this.m_registers = new Array(b2Shape.e_shapeTypeCount);
    for (let i = 0; i < b2Shape.e_shapeTypeCount; i += 1) {
      this.m_registers[i] = new Array(b2Shape.e_shapeTypeCount);
      for (let j = 0; j < b2Shape.e_shapeTypeCount; j += 1) {
        this.m_registers[i][j] = new b2ContactRegister();
      }
    }
    this.AddType(b2CircleContact.Create, b2CircleContact.Destroy, b2Shape.e_circleShape, b2Shape.e_circleShape);
    this.AddType(b2PolyAndCircleContact.Create, b2PolyAndCircleContact.Destroy, b2Shape.e_polygonShape, b2Shape.e_circleShape);
    this.AddType(b2PolygonContact.Create, b2PolygonContact.Destroy, b2Shape.e_polygonShape, b2Shape.e_polygonShape);
  }

  Create(fixtureA, fixtureB) {
    const type1 = fixtureType(fixtureA);
    const type2 = fixtureType(fixtureB);
    const reg = this.m_registers[type1][type2];
    let contact;
    if (reg.pool) {
      contact = reg.pool;
      reg.pool = contact.m_next;
      reg.poolCount -= 1;
      contact.Reset(fixtureA, fixtureB);
      return contact;
    }
    const createFcn = reg.createFcn;
    if (createFcn != null) {
      contact = createFcn(this.m_allocator);
      if (reg.primary) {
        contact.Reset(fixtureA, fixtureB);
      } else {
        contact.Reset(fixtureB, fixtureA);
      }
      return contact;
    }
    return null;
  }

  Destroy(contact) {
    if (contact.m_manifold?.m_pointCount > 0) {
      contact.m_fixtureA?.m_body?.SetAwake?.(true);
      contact.m_fixtureB?.m_body?.SetAwake?.(true);
    }
    const type1 = fixtureType(contact.m_fixtureA);
    const type2 = fixtureType(contact.m_fixtureB);
    const reg = this.m_registers[type1][type2];
    reg.poolCount = (reg.poolCount ?? 0) + 1;
    contact.m_next = reg.pool;
    reg.pool = contact;
    reg.destroyFcn(contact, this.m_allocator);
  }
}

export class b2CircleContact extends b2Contact {
  constructor(shape1, shape2) {
    super(shape1, shape2);
    this.m_manifolds = [new b2Manifold()];
    this.m_manifold = this.m_manifolds[0];
    this.m0 = new b2Manifold();
    this.m_manifold.pointCount = 0;
    const point = this.m_manifold.points[0];
    point.normalImpulse = 0;
    point.tangentImpulse = 0;
  }

  static Destroy(contact, allocator) {
    void contact;
    void allocator;
  }

  static Create(shape1, shape2, allocator) {
    void allocator;
    return new b2CircleContact(shape1, shape2);
  }

  Evaluate(listener) {
    const body1 = this.m_shape1.m_body;
    const body2 = this.m_shape2.m_body;
    this.m0.Set(this.m_manifold);
    b2Collision.b2CollideCircles(this.m_manifold, this.m_shape1, body1.m_xf, this.m_shape2, body2.m_xf);
    const cp = new b2ContactPoint();
    cp.shape1 = this.m_shape1;
    cp.shape2 = this.m_shape2;
    cp.friction = this.m_friction;
    cp.restitution = this.m_restitution;
    if ((this.m_manifold.pointCount ?? 0) > 0) {
      this.m_manifoldCount = 1;
      const p = this.m_manifold.points[0];
      if ((this.m0.pointCount ?? 0) === 0) {
        p.normalImpulse = 0;
        p.tangentImpulse = 0;
        if (listener) {
          cp.position = body1.GetWorldPoint(p.localPoint1);
          const v1 = body1.GetLinearVelocityFromLocalPoint(p.localPoint1);
          const v2 = body2.GetLinearVelocityFromLocalPoint(p.localPoint2);
          cp.velocity.Set(v2.x - v1.x, v2.y - v1.y);
          cp.normal.SetV(this.m_manifold.normal);
          cp.separation = p.separation;
          cp.id.key = p.id._key;
          listener.Add(cp);
        }
      } else {
        const oldP = this.m0.points[0];
        p.normalImpulse = oldP.normalImpulse;
        p.tangentImpulse = oldP.tangentImpulse;
        if (listener) {
          cp.position = body1.GetWorldPoint(p.localPoint1);
          const v1 = body1.GetLinearVelocityFromLocalPoint(p.localPoint1);
          const v2 = body2.GetLinearVelocityFromLocalPoint(p.localPoint2);
          cp.velocity.Set(v2.x - v1.x, v2.y - v1.y);
          cp.normal.SetV(this.m_manifold.normal);
          cp.separation = p.separation;
          cp.id.key = p.id._key;
          listener.Persist(cp);
        }
      }
    } else {
      this.m_manifoldCount = 0;
      if ((this.m0.pointCount ?? 0) > 0 && listener) {
        const oldP = this.m0.points[0];
        cp.position = body1.GetWorldPoint(oldP.localPoint1);
        const v1 = body1.GetLinearVelocityFromLocalPoint(oldP.localPoint1);
        const v2 = body2.GetLinearVelocityFromLocalPoint(oldP.localPoint2);
        cp.velocity.Set(v2.x - v1.x, v2.y - v1.y);
        cp.normal.SetV(this.m0.normal);
        cp.separation = oldP.separation;
        cp.id.key = oldP.id._key;
        listener.Remove(cp);
      }
    }
  }

  GetManifolds() {
    return this.m_manifolds;
  }
}

export class b2PolygonContact extends b2Contact {
  constructor(shape1, shape2) {
    super(shape1, shape2);
    this.m_manifolds = [new b2Manifold()];
    this.m0 = new b2Manifold();
    this.m_manifold = this.m_manifolds[0];
    this.m_manifold.pointCount = 0;
  }

  static Destroy(contact, allocator) {
    void contact;
    void allocator;
  }

  static Create(shape1, shape2, allocator) {
    void allocator;
    return new b2PolygonContact(shape1, shape2);
  }

  Evaluate(listener) {
    const body1 = this.m_shape1.m_body;
    const body2 = this.m_shape2.m_body;
    this.m0.Set(this.m_manifold);
    b2Collision.b2CollidePolygons(this.m_manifold, this.m_shape1, body1.m_xf, this.m_shape2, body2.m_xf);
    const persisted = [false, false];
    const cp = new b2ContactPoint();
    cp.shape1 = this.m_shape1;
    cp.shape2 = this.m_shape2;
    cp.friction = this.m_friction;
    cp.restitution = this.m_restitution;
    if ((this.m_manifold.pointCount ?? 0) > 0) {
      for (let i = 0; i < this.m_manifold.pointCount; i += 1) {
        const p = this.m_manifold.points[i];
        p.normalImpulse = 0;
        p.tangentImpulse = 0;
        let found = false;
        const idKey = p.id._key;
        for (let j = 0; j < (this.m0.pointCount ?? 0); j += 1) {
          if (persisted[j] === true) continue;
          const oldP = this.m0.points[j];
          if (oldP.id._key === idKey) {
            persisted[j] = true;
            p.normalImpulse = oldP.normalImpulse;
            p.tangentImpulse = oldP.tangentImpulse;
            found = true;
            if (listener) {
              cp.position = body1.GetWorldPoint(p.localPoint1);
              const v1 = body1.GetLinearVelocityFromLocalPoint(p.localPoint1);
              const v2 = body2.GetLinearVelocityFromLocalPoint(p.localPoint2);
              cp.velocity.Set(v2.x - v1.x, v2.y - v1.y);
              cp.normal.SetV(this.m_manifold.normal);
              cp.separation = p.separation;
              cp.id.key = idKey;
              listener.Persist(cp);
            }
            break;
          }
        }
        if (!found && listener) {
          cp.position = body1.GetWorldPoint(p.localPoint1);
          const v1 = body1.GetLinearVelocityFromLocalPoint(p.localPoint1);
          const v2 = body2.GetLinearVelocityFromLocalPoint(p.localPoint2);
          cp.velocity.Set(v2.x - v1.x, v2.y - v1.y);
          cp.normal.SetV(this.m_manifold.normal);
          cp.separation = p.separation;
          cp.id.key = idKey;
          listener.Add(cp);
        }
      }
      this.m_manifoldCount = 1;
    } else {
      this.m_manifoldCount = 0;
    }
    if (!listener) return;
    for (let i = 0; i < (this.m0.pointCount ?? 0); i += 1) {
      if (persisted[i]) continue;
      const oldP = this.m0.points[i];
      cp.position = body1.GetWorldPoint(oldP.localPoint1);
      const v1 = body1.GetLinearVelocityFromLocalPoint(oldP.localPoint1);
      const v2 = body2.GetLinearVelocityFromLocalPoint(oldP.localPoint2);
      cp.velocity.Set(v2.x - v1.x, v2.y - v1.y);
      cp.normal.SetV(this.m0.normal);
      cp.separation = oldP.separation;
      cp.id.key = oldP.id._key;
      listener.Remove(cp);
    }
  }

  GetManifolds() {
    return this.m_manifolds;
  }
}

export class b2PolyAndCircleContact extends b2Contact {
  constructor(shape1, shape2) {
    super(shape1, shape2);
    this.m_manifolds = [new b2Manifold()];
    this.m_manifold = this.m_manifolds[0];
    this.m0 = new b2Manifold();
    this.m_manifold.pointCount = 0;
    const point = this.m_manifold.points[0];
    point.normalImpulse = 0;
    point.tangentImpulse = 0;
  }

  static Destroy(contact, allocator) {
    void contact;
    void allocator;
  }

  static Create(shape1, shape2, allocator) {
    void allocator;
    return new b2PolyAndCircleContact(shape1, shape2);
  }

  Evaluate(listener) {
    const body1 = this.m_shape1.m_body;
    const body2 = this.m_shape2.m_body;
    this.m0.Set(this.m_manifold);
    b2Collision.b2CollidePolygonAndCircle(this.m_manifold, this.m_shape1, body1.m_xf, this.m_shape2, body2.m_xf);
    const persisted = [false, false];
    const cp = new b2ContactPoint();
    cp.shape1 = this.m_shape1;
    cp.shape2 = this.m_shape2;
    cp.friction = this.m_friction;
    cp.restitution = this.m_restitution;
    if ((this.m_manifold.pointCount ?? 0) > 0) {
      for (let i = 0; i < this.m_manifold.pointCount; i += 1) {
        const p = this.m_manifold.points[i];
        p.normalImpulse = 0;
        p.tangentImpulse = 0;
        let found = false;
        const idKey = p.id._key;
        for (let j = 0; j < (this.m0.pointCount ?? 0); j += 1) {
          if (persisted[j] === true) continue;
          const oldP = this.m0.points[j];
          if (oldP.id._key === idKey) {
            persisted[j] = true;
            p.normalImpulse = oldP.normalImpulse;
            p.tangentImpulse = oldP.tangentImpulse;
            found = true;
            if (listener) {
              cp.position = body1.GetWorldPoint(p.localPoint1);
              const v1 = body1.GetLinearVelocityFromLocalPoint(p.localPoint1);
              const v2 = body2.GetLinearVelocityFromLocalPoint(p.localPoint2);
              cp.velocity.Set(v2.x - v1.x, v2.y - v1.y);
              cp.normal.SetV(this.m_manifold.normal);
              cp.separation = p.separation;
              cp.id.key = idKey;
              listener.Persist(cp);
            }
            break;
          }
        }
        if (!found && listener) {
          cp.position = body1.GetWorldPoint(p.localPoint1);
          const v1 = body1.GetLinearVelocityFromLocalPoint(p.localPoint1);
          const v2 = body2.GetLinearVelocityFromLocalPoint(p.localPoint2);
          cp.velocity.Set(v2.x - v1.x, v2.y - v1.y);
          cp.normal.SetV(this.m_manifold.normal);
          cp.separation = p.separation;
          cp.id.key = idKey;
          listener.Add(cp);
        }
      }
      this.m_manifoldCount = 1;
    } else {
      this.m_manifoldCount = 0;
    }
    if (!listener) return;
    for (let i = 0; i < (this.m0.pointCount ?? 0); i += 1) {
      if (persisted[i]) continue;
      const oldP = this.m0.points[i];
      cp.position = body1.GetWorldPoint(oldP.localPoint1);
      const v1 = body1.GetLinearVelocityFromLocalPoint(oldP.localPoint1);
      const v2 = body2.GetLinearVelocityFromLocalPoint(oldP.localPoint2);
      cp.velocity.Set(v2.x - v1.x, v2.y - v1.y);
      cp.normal.SetV(this.m0.normal);
      cp.separation = oldP.separation;
      cp.id.key = oldP.id._key;
      listener.Remove(cp);
    }
  }

  GetManifolds() {
    return this.m_manifolds;
  }
}
